import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Leaf, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { LanguageDropdown, LanguageBar } from './LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';
import { productCategories } from '../mockData';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const isActive = (path) => location.pathname === path;
  const navClass = (active) => `text-base font-medium transition-colors hover:text-[#8B2C3A] ${active ? 'text-[#8B2C3A]' : 'text-gray-700'}`;

  const productLinks = productCategories.map(c => ({ slug: c.slug, name: t(`categories.${c.slug}.name`), path: `/products/${c.slug}` }));

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      {/* Eco-Friendly Badge Banner */}
      <div className="bg-emerald-600 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 text-sm font-medium">
          <span className="hidden sm:block w-24" />
          <div className="flex items-center justify-center gap-2 flex-1 sm:flex-none">
            <Leaf className="w-4 h-4 flex-shrink-0" />
            <span data-testid="eco-banner-text">{t('banner')}</span>
          </div>
          <LanguageBar />
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_vbglobal-portal/artifacts/k1dc2y0l_VB%20GLOBAL%20LOGO.jpg"
              alt="VB Global Logo"
              className="h-32 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" data-testid="nav-home" className={navClass(isActive('/'))}>{t('nav.home')}</Link>

            <div className="relative" onMouseEnter={() => setIsProductsOpen(true)} onMouseLeave={() => setIsProductsOpen(false)}>
              <button data-testid="nav-products" className={`${navClass(location.pathname.startsWith('/products'))} flex items-center gap-1`}>
                {t('nav.products')}
                <ChevronDown className="w-4 h-4" />
              </button>
              {isProductsOpen && (
                <div className="absolute top-full left-0 w-64 pt-2">
                  <div className="bg-white rounded-lg shadow-lg py-2 border border-gray-200">
                    {productLinks.map((product) => (
                      <Link
                        key={product.path}
                        to={product.path}
                        data-testid={`nav-product-link-${product.slug}`}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#8B2C3A] transition-colors"
                      >
                        {product.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/blog" data-testid="nav-blog" className={navClass(location.pathname.startsWith('/blog'))}>{t('nav.blog')}</Link>
            <Link to="/solutions" data-testid="nav-solutions" className={navClass(isActive('/solutions'))}>{t('nav.solutions')}</Link>
            <Link to="/about" data-testid="nav-about" className={navClass(isActive('/about'))}>{t('nav.about')}</Link>

            <Link to="/contact" data-testid="nav-contact">
              <Button className="bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white">{t('nav.contact')}</Button>
            </Link>

            <LanguageDropdown />
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-base font-medium text-gray-700 hover:text-[#8B2C3A]" onClick={() => setIsMenuOpen(false)}>{t('nav.home')}</Link>

              <div className="flex flex-col space-y-2">
                <span className="text-base font-medium text-gray-900">{t('nav.products')}</span>
                {productLinks.map((product) => (
                  <Link
                    key={product.path}
                    to={product.path}
                    className="text-sm text-gray-600 hover:text-[#8B2C3A] pl-4"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {product.name}
                  </Link>
                ))}
              </div>

              <Link to="/blog" className="text-base font-medium text-gray-700 hover:text-[#8B2C3A]" onClick={() => setIsMenuOpen(false)}>{t('nav.blog')}</Link>
              <Link to="/solutions" className="text-base font-medium text-gray-700 hover:text-[#8B2C3A]" onClick={() => setIsMenuOpen(false)}>{t('nav.solutions')}</Link>
              <Link to="/about" className="text-base font-medium text-gray-700 hover:text-[#8B2C3A]" onClick={() => setIsMenuOpen(false)}>{t('nav.about')}</Link>

              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white">{t('nav.contact')}</Button>
              </Link>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-500">{t('nav.language')}</span>
                <LanguageDropdown />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
